import { nanoid } from 'nanoid';

import { IWorkBenchLayout } from '@fox/blocks/contracts/work-bench';
import { CREATE_PEOPLE_BLOCK, CREATE_PERSON_BLOCK, TRANSACTION_BLOCK_TYPE } from '../../form-block-types';

import { tryParseJSONObject } from '@fox/atoms/utils/string';
import { FormQuestionTypes } from '../blocks/question-block/contracts';
import { prefillStringAnswer } from '../blocks/question-block/question-block';
import { defaultCustomFieldTableAnswer } from '../blocks/question-block/question-renders/data-table/helpers';
import { prefillTransactionAnswer } from '../blocks/transaction-block/transaction-block';
import { IQuestionAnswer } from '../form-root/contracts';
import { IDefaultAnswerProviderFunction } from './contracts';

function prefillCreatePersonAnswer(query: URLSearchParams, question: any, initialAnswer?: IQuestionAnswer): IQuestionAnswer | undefined {
	const queryFirstName = query.get('firstname');
	const queryLastName = query.get('lastname');
	const queryEmail = query.get('email');

	const initialContacts = tryParseJSONObject(initialAnswer?.answer?.answerJson)?.contacts?.[0] ?? {};

	const firstName = initialContacts.firstName ?? queryFirstName;
	const lastName = initialContacts.lastName ?? queryLastName;
	const email = initialContacts.email ?? queryEmail;

	if (firstName || lastName || email) {
		const userInfo: Record<string, string> = {};
		if (firstName) userInfo.firstName = firstName;
		if (lastName) userInfo.lastName = lastName;
		if (email) userInfo.email = email;

		return {
			questionName: question.name,
			answer: {
				answerJson: JSON.stringify({ contacts: [{ id: nanoid(), ...userInfo }] })
			},
			type: question.type
		};
	}
}

// These are special functions that can prefill answers based on URL query parameters.
// They will take into account the metadata of the question to determine how to prefill it.
const prefillAnswer: Record<string, (query: URLSearchParams, question: any, initialAnswer?: IQuestionAnswer) => IQuestionAnswer | undefined> = {
	[FormQuestionTypes.Text]: prefillStringAnswer,
	[FormQuestionTypes.SingleChoice]: prefillStringAnswer,
	[TRANSACTION_BLOCK_TYPE]: prefillTransactionAnswer,
	[CREATE_PERSON_BLOCK]: prefillCreatePersonAnswer,
	[CREATE_PEOPLE_BLOCK]: prefillCreatePersonAnswer
};

// These are special functions that determine the default answer for a question based on settings
// They will take into account the metadata of the question to determine how to get the default answer.
const defaultAnswer: Record<string, IDefaultAnswerProviderFunction> = {
	[FormQuestionTypes.Date]: () => undefined, //TODO: Replace the existing useDefaultAnswer hook with this
	[FormQuestionTypes.DataTable]: defaultCustomFieldTableAnswer
};

export function prefillAnswers(layout: IWorkBenchLayout, initialAnswers?: IQuestionAnswer[]): IQuestionAnswer[] | undefined {
	// find all questions
	const fields = layout.layouts[0].props.sections.flatMap(s => s.fields);

	//Prefer initialAnswers, then query parameters, if neither are available, use default answers
	const prefilledAnswers: IQuestionAnswer[] = [];
	const query = new URLSearchParams(window.location.search);
	for (const field of fields) {
		if (!field?.type) continue; //Skip if type is not defined

		const answer =
			prefillAnswer[field.type]?.(
				query,
				field,
				initialAnswers?.find(a => a.questionName === field.name)
			) || defaultAnswer[field.type]?.(field);
		if (answer) {
			prefilledAnswers.push(answer);
		}
	}

	return prefilledAnswers.length > 0 ? prefilledAnswers : undefined;
}
